import React, { Dispatch, SetStateAction, useEffect, useCallback, useMemo } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { startOfTomorrow, isBefore } from "date-fns";
import { useTable, useExpanded, Row } from "react-table";
import {
  TimesheetsFilterOptions,
  TableCollapseFormat,
  TimesheetsFiltersType,
  TimesheetTabs,
} from "../../types/Payroll.types";
import { TimesheetsShiftInfo } from "../../types/Shift.types";
import {
  filterTableDataByTabIndex,
  generateCollapseData,
  parsePayrollFilterOptions,
  applyPayrollFilters,
} from "../../payrollDataProcessors";
import { TableCollapseGroupRow } from "./TableCollapseGroupRow";
import { StyledTable } from "./styledComponents";
import { TableColumns } from "./TableColumns";
import { TableStageRow } from "./TableStageRow";
import { EmptySearch } from "../TimesheetsPageSearchWarning";
import { MAIN_SCROLLABLE_PANEL } from "../../../../utility";

// TODO: FIN-407 - Add Test Coverage - https://ebpp.atlassian.net/browse/FIN-407

export const TimesheetsIconPathPrefix = `${import.meta.env.VITE_ASSET_BASE_URL}/icons/Timesheets`;

export type TimesheetsTableProps = {
  activeIndex: TimesheetTabs;
  setFilterOptions: Dispatch<SetStateAction<TimesheetsFilterOptions>>;
  tableData: TimesheetsShiftInfo[];
  tableFilters: TimesheetsFiltersType;
};

export const TimesheetsTable = ({
  activeIndex,
  setFilterOptions,
  tableData,
  tableFilters,
}: TimesheetsTableProps) => {
  const { tabs, hasRows, calculatedFilterOptions } = useMemo(() => {
    const truncatedTableData = tableData.filter((value) => {
      return isBefore(value.shiftThresholds.expectedStart, startOfTomorrow());
    });
    const activeIndexData = filterTableDataByTabIndex(truncatedTableData, activeIndex);
    const calculatedFilterOptions = parsePayrollFilterOptions(activeIndexData);

    const filteredView = applyPayrollFilters(activeIndexData, tableFilters);

    const hasRows = !!filteredView.length;

    const tabs: TableCollapseFormat[] = generateCollapseData(filteredView);

    return { tabs, hasRows, calculatedFilterOptions };
  }, [tableData, activeIndex, tableFilters, startOfTomorrow().toISOString()]);

  // filter options not reduced by active filters b.c. you can only select one at a time
  useEffect(() => {
    setFilterOptions(calculatedFilterOptions); // maybe reduce at later date?
  }, [startOfTomorrow().toISOString(), activeIndex]);

  const hasSearchString = !!tableFilters.search.userName;

  const tabsInitialState: Record<string, boolean> = {};
  tabs.forEach((_, i) => {
    tabsInitialState[`${i}`] = true
  });

  const columns = useMemo(() => TableColumns, [activeIndex]);
  const { getTableProps, getTableBodyProps, rows, prepareRow } = useTable({
    columns,
    data: tabs,
    initialState: {
      expanded: tabsInitialState
    },
    getSubRows: useCallback((row) => 'rows' in row ? row.rows : [], []),
  }, useExpanded);

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: useCallback(() => document.getElementById(MAIN_SCROLLABLE_PANEL), []),
    estimateSize: useCallback((index) => 'rows' in rows[index].original ? 32 : 80, [rows]),
    overscan: 6,
  });

  useEffect(() => {
    rowVirtualizer.measure()
  }, [rows]);

  return (
    <>
      {!rowVirtualizer.scrollElement && <div>MAIN_SCROLLABLE_PANEL not loaded</div>}
      <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
        <StyledTable {...getTableProps()}>
          <tbody {...getTableBodyProps()}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              prepareRow(row);
              const originalData = row.original;

              // Essential positioning styles for virtual scrolling
              const positionStyleData = {
                position: 'absolute' as const,
                top: 0,
                left: 0,
                height: `${virtualRow.size}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }

              if ('rows' in originalData) {
                const groupKey = `group-${originalData.headerDate}`
                return (
                  <TableCollapseGroupRow
                    key={groupKey}
                    row={row as Row<TableCollapseFormat>}
                    colSpan={columns.length}
                    style={positionStyleData}
                  />
                );
              }

              const key = `${originalData.job.externalId}-${originalData.job.shiftIndex}-${originalData.user.externalId}`;
              return <TableStageRow key={key} row={row as Row<TimesheetsShiftInfo>} activeIndex={activeIndex} style={positionStyleData} />;
            })}
          </tbody>
        </StyledTable>
      </div>
      {!hasRows && hasSearchString && (
        <EmptySearch path={TimesheetsIconPathPrefix} message={"No results based on your search"} />
      )}
      {!hasRows && !hasSearchString && (
        <EmptySearch path={TimesheetsIconPathPrefix} message={"No results"} />
      )}
    </>
  );
};
